import { useState } from 'react';
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  AccountTree,
  PrecisionManufacturing,
  Groups,
  GridOn,
  Build,
  Person,
  Insights,
  ChevronLeft,
  ChevronRight,
} from '@mui/icons-material';
import { useNavigate, useLocation, Navigate, Routes, Route } from 'react-router-dom';
import { ROUTES } from '../../constants/routes';
import { ErrorBoundary } from '../../components/shared/ErrorBoundary';
import { MasterGanttPage } from './views/MasterGanttPage';
import { MachineSchedulePage } from './views/MachineSchedulePage';
import { EmployeeSchedulePage } from './views/EmployeeSchedulePage';
import { ScenarioPlanningPage } from './views/ScenarioPlanningPage';
import { SkillMatrixPage } from './views/SkillMatrixPage';
import { MachinesPage } from './views/MachinesPage';
import { MySchedulePage } from './views/MySchedulePage';
// TaskTablePage is kept in the repo (still used by tests / future WO flows) but
// its route is now a redirect — all /schedule/tasks?… traffic is forwarded to
// Scenario Planning where resolveScenarioForSource handles both Quote and WO sources.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { TaskTablePage } from './tasks/TaskTablePage';
import { SCHEDULE_CONTENT_PADDING } from './views/scheduleGridShared';

/**
 * FEAT-2026-0007 6E.2 — Redirect /schedule/tasks → /schedule/scenario-planning.
 *
 * Preserves the full query string so bookmarks and deep-links resume correctly:
 *   /schedule/tasks?source=Quote&sourceKey=X      → scenario-planning?source=Quote&sourceKey=X
 *   /schedule/tasks?source=WorkOrder&sourceKey=X  → scenario-planning?source=WorkOrder&sourceKey=X
 *
 * The deep-link resolver (resolveScenarioForSource) already handles both source
 * types, so WO-only flows continue to work transparently.
 */
const TaskTableRedirect = (): JSX.Element => {
  const { search } = useLocation();
  return <Navigate to={`${ROUTES.SCHEDULE_SCENARIO}${search}`} replace />;
};

const SIDE_NAV_WIDTH = 220;
const SIDE_NAV_COLLAPSED_WIDTH = 56;
/** localStorage key so the collapsed/expanded choice survives navigation + reloads. */
const SIDE_NAV_STORAGE_KEY = 'belab.schedule.sidenav.collapsed';

interface NavEntry {
  label: string;
  path: string;
  icon: React.ReactNode;
}

const NAV_ENTRIES: NavEntry[] = [
  { label: 'Master Gantt', path: ROUTES.SCHEDULE_MASTER_GANTT, icon: <AccountTree fontSize="small" /> },
  { label: 'Machine Schedule', path: ROUTES.SCHEDULE_MACHINE, icon: <PrecisionManufacturing fontSize="small" /> },
  { label: 'Employee Schedule', path: ROUTES.SCHEDULE_EMPLOYEE, icon: <Groups fontSize="small" /> },
  { label: 'Scenario Planning', path: ROUTES.SCHEDULE_SCENARIO, icon: <Insights fontSize="small" /> },
  { label: 'Skill Matrix', path: ROUTES.SCHEDULE_SKILL_MATRIX, icon: <GridOn fontSize="small" /> },
  { label: 'Machines', path: ROUTES.SCHEDULE_MACHINES, icon: <Build fontSize="small" /> },
  { label: 'My Schedule', path: ROUTES.SCHEDULE_MY, icon: <Person fontSize="small" /> },
];

export const SchedulePage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  // Collapsible left side-nav. Persisted so the choice is sticky across views.
  const [collapsed, setCollapsed] = useState<boolean>(
    () => localStorage.getItem(SIDE_NAV_STORAGE_KEY) === '1',
  );

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(SIDE_NAV_STORAGE_KEY, next ? '1' : '0');
      return next;
    });
  };

  return (
    // Fixed-height shell: the outer row exactly fills the viewport below the
    // app bar (mt/mb:-2 cancel AppShell's vertical padding) and never scrolls
    // itself. Only the main-content column scrolls, so the side-nav stays
    // docked and always visible. We don't use position:sticky here because
    // AppShell's <main> sets overflowX:hidden, which makes it a scroll
    // container and would break window-relative sticky.
    <Box sx={{ display: 'flex', height: 'calc(100vh - 60px)', mx: -3, mt: -2, mb: -2, overflow: 'hidden' }}>
      {/* Left side-nav — collapsible to an icon-only rail. Full-height +
          internal scroll so it stays docked even on short screens. */}
      <Box
        sx={{
          width: collapsed ? SIDE_NAV_COLLAPSED_WIDTH : SIDE_NAV_WIDTH,
          flexShrink: 0,
          borderRight: `1px solid ${theme.border.color}`,
          backgroundColor: theme.palette.background.paper,
          display: 'flex',
          flexDirection: 'column',
          pt: 2,
          overflowX: 'hidden',
          overflowY: 'auto',
          // Smooth width animation when collapsing/expanding.
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.standard,
          }),
        }}
      >
        {/* Header row: "Schedule" caption (hidden when collapsed) + toggle button */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'space-between',
            px: collapsed ? 0 : 2,
            pb: 1,
            minHeight: 24,
          }}
        >
          {!collapsed && (
            <Typography
              variant="overline"
              sx={{
                fontSize: '0.65rem',
                fontWeight: 700,
                letterSpacing: 1.2,
                color: alpha(theme.palette.text.primary, 0.45),
                whiteSpace: 'nowrap',
              }}
            >
              Schedule
            </Typography>
          )}
          <Tooltip title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} placement="right">
            <IconButton
              size="small"
              onClick={toggleCollapsed}
              aria-label={collapsed ? 'Expand schedule sidebar' : 'Collapse schedule sidebar'}
              sx={{ color: alpha(theme.palette.text.primary, 0.55) }}
            >
              {collapsed ? <ChevronRight fontSize="small" /> : <ChevronLeft fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
        <Divider sx={{ mx: 1, mb: 1 }} />
        <List dense disablePadding sx={{ px: 1 }}>
          {NAV_ENTRIES.map((entry) => {
            const isActive = location.pathname === entry.path;
            return (
              <Tooltip
                key={entry.path}
                title={collapsed ? entry.label : ''}
                placement="right"
                disableHoverListener={!collapsed}
              >
                <ListItemButton
                  selected={isActive}
                  onClick={() => navigate(entry.path)}
                  sx={{
                    borderRadius: '8px',
                    mb: 0.25,
                    py: 0.75,
                    px: collapsed ? 0 : 1.25,
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    borderLeft: `3px solid ${isActive ? theme.palette.primary.main : 'transparent'}`,
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.04),
                      borderLeftColor: alpha(theme.palette.primary.main, 0.3),
                    },
                    '&.Mui-selected': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.08),
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      },
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: collapsed ? 0 : 32,
                      justifyContent: 'center',
                      color: isActive ? theme.palette.primary.main : alpha(theme.palette.text.primary, 0.5),
                    }}
                  >
                    {entry.icon}
                  </ListItemIcon>
                  {!collapsed && (
                    <ListItemText
                      primary={entry.label}
                      primaryTypographyProps={{
                        fontSize: '0.8rem',
                        fontWeight: isActive ? 600 : 400,
                        color: isActive ? theme.palette.text.primary : alpha(theme.palette.text.primary, 0.75),
                        noWrap: true,
                      }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            );
          })}
        </List>
      </Box>

      {/* Main content — THE scroll container for every schedule view. Sticky
          elements inside pin to its CONTENT edge, i.e. SCHEDULE_CONTENT_PADDING
          below its visual top; page-flow sticky headers offset by its negative
          to pin flush (Phase1Panel task table). */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          p: SCHEDULE_CONTENT_PADDING, // shared constant — sticky headers offset by its negative
          backgroundColor: theme.palette.background.default,
          overflow: 'auto',
        }}
      >
        {/* v1.0.09: contain any schedule-view render crash to this content column
            (side-nav + app bar stay alive) instead of blanking the whole SPA.
            resetKey=pathname → navigating to another view clears the error. */}
        <ErrorBoundary resetKey={location.pathname} label="this schedule page">
          <Routes>
            <Route index element={<Navigate to={ROUTES.SCHEDULE_MASTER_GANTT} replace />} />
            <Route path="master-gantt" element={<MasterGanttPage />} />
            <Route path="machine-schedule" element={<MachineSchedulePage />} />
            <Route path="employee-schedule" element={<EmployeeSchedulePage />} />
            {/* FEAT-2026-0007 Phase 6B: Scenario Planning wizard */}
            <Route path="scenario-planning" element={<ScenarioPlanningPage />} />
            <Route path="skill-matrix" element={<SkillMatrixPage />} />
            <Route path="machines" element={<MachinesPage />} />
            <Route path="my-schedule" element={<MySchedulePage />} />
            {/* FEAT-2026-0007 6E.2: /schedule/tasks is retired as a page entry point.
                Redirect to Scenario Planning, preserving ?source=&sourceKey= so
                bookmarks and WO deep-links resume in the wizard. TaskTablePage.tsx
                is kept in the repo — do not delete; it may be referenced again. */}
            <Route path="tasks" element={<TaskTableRedirect />} />
          </Routes>
        </ErrorBoundary>
      </Box>
    </Box>
  );
};
